/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _toInteger from 'lodash/toInteger';
import _findIndex from 'lodash/findIndex';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _parseInt from 'lodash/parseInt';
import _sortBy from 'lodash/sortBy';
import _maxBy from 'lodash/maxBy';
import Card from '@mui/material/Card';
import MLButton from '../../MLButton/MLButton';
import MLIcon from '../../MLIcon/MLIcon';
import MLRadioButtonGroup from '../../MLRadio/MLRadioButtonGroup';
import MLRadioButton from '../..//MLRadio/MLRadioButton';
import MLCheckBox from '../../MLCheckBox/MLCheckBox';
import MLSelect from '../../MLSelect';
import MLFullScreenDialog from '../../MLFullScreenDialog/MLFullScreenDialog';
import OverLabledTextBox from '../../../components/OverLabledTextBox/OverLabledTextBox';
import {
  extractSettingAs,
  GRADEBOOK_SETTINGS,
} from '@macmillan-learning/gradebook-shared';
import { updateGradebookSettingsAndCategories } from '../../../actions/updateGradebook';
import { updateAlert } from '../../../actions/gradebook';
import { selectGradebookCategories } from '../../../selectors/categories';
import { getNCategoryAssignmentsCountDropdown } from '../../../utils/InstructorGradebookHelpers';
import { getConfigObject } from '../../../utils/config.js';
import MessageBanner from '../../InstructorGradebookContainer/Components/MessageBanner';
import CategoryOrder from '../CategoryOrder/CategoryOrder';

import {
  StyledDialogContent,
  modalActionButtonStyles,
} from './GradebookSettingsStyles.js';
import s from './GradeookSettings.module.css';
import {
  changeGradeDisplay,
  changeGradingType,
  clickAddCategory,
  clickRemoveCategory,
  clickReportInProgressWork,
  clickRequireStudentId,
  clickSaveGradebookSettings,
} from '../../../actions/analyticsEvents';
import { sendAnalyticsEvent } from '../../../actions/sendAnalytics';

function GradebookSettingsDialog({
  title,
  isOpen,
  weightTotal,
  gradingType,
  handleClose,
  handleSave,
  handleExited,
  isSettingsUpdating,
  isDisabled,
  children,
  disableEnforceFocus,
}) {
  const actions = (
    <>
      <MLButton
        btnText="Cancel"
        btnType="secondary"
        testId="cancel-category-button"
        style={modalActionButtonStyles}
        onClick={handleClose}
      />
      {!isDisabled && (
        <MLButton
          btnText="Save"
          btnType="primary"
          testId="save-category-button"
          style={{ ...modalActionButtonStyles, marginLeft: 15 }}
          onClick={handleSave}
          disabled={
            isSettingsUpdating ||
            (weightTotal !== 100 &&
              gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted)
          }
        />
      )}
    </>
  );

  return (
    <MLFullScreenDialog
      open={isOpen}
      onClose={handleClose}
      onExited={handleExited}
      title={title}
      actions={actions}
      disableEnforceFocus={disableEnforceFocus}
      data-test-id="gb-settings-dialog"
    >
      <StyledDialogContent>{children}</StyledDialogContent>
    </MLFullScreenDialog>
  );
}
GradebookSettingsDialog.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSettingsUpdating: PropTypes.bool.isRequired,
  weightTotal: PropTypes.number.isRequired,
  gradingType: PropTypes.number.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleExited: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  children: PropTypes.any,
  disableEnforceFocus: PropTypes.bool,
};

class GradebookSettings extends Component {
  static propTypes = {
    isOpen: PropTypes.bool,
    handleSettingsClose: PropTypes.func,
    handleModalExited: PropTypes.func,
    rdxGradebookSettings: PropTypes.array,
    rdxGradebookCategories: PropTypes.array,
    courseId: PropTypes.string,
    save: PropTypes.func,
    // eslint-disable-next-line react/no-unused-prop-types
    cancel: PropTypes.func,
    user: PropTypes.object,
    isRestrictedSection: PropTypes.bool,
    isSectionManager: PropTypes.bool,
    isSettingsUpdating: PropTypes.bool,
    showSessionManagerOnSave: PropTypes.func,
    hasInstructorLinkedCourse: PropTypes.bool,
    sendSettingsEvent: PropTypes.func,
    isDisabled: PropTypes.bool,
    disableEnforceFocus: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      settings: props.rdxGradebookSettings || [],
      gradebookCategories: props.rdxGradebookCategories || [],
      gradebookCategoryErrors: [],
      categoryAdded: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.rdxGradebookSettings.length === 0 &&
      this.props.rdxGradebookSettings.length > 0
    ) {
      this.setState({ settings: this.props.rdxGradebookSettings });
    }
    if (
      prevProps.rdxGradebookCategories.length === 0 &&
      this.props.rdxGradebookCategories.length > 0
    ) {
      this.setState({ gradebookCategories: this.props.rdxGradebookCategories });
    }
  }

  onSave = () => {
    const { sendSettingsEvent, courseId } = this.props;
    const { gradebookCategories } = this.state;
    sendSettingsEvent(clickSaveGradebookSettings(courseId));
    const gradebookCategoryErrors = [];
    gradebookCategories.forEach((gb, i) => {
      if (
        !gb.__destroyed &&
        (this.isEmptyOrNaN(gb.drop_lowest_num_grades) ||
          this.isEmptyString(gb.name))
      ) {
        gradebookCategoryErrors.push({
          index: i,
          errorMessage: 'Categories must have a name',
        });
      }
    });
    if (gradebookCategoryErrors.length)
      return this.setState({ gradebookCategoryErrors });
    return this.pushUpdates();
  };

  onChangeDropLowestGrade = (stateIndex, dropLowest) => {
    const { gradebookCategoryErrors } = this.state;
    this.alterCategory(stateIndex, { drop_lowest_num_grades: dropLowest });
    if (!this.isEmptyOrNaN(dropLowest)) {
      this.setState({
        gradebookCategoryErrors: [
          ...gradebookCategoryErrors.slice(0, stateIndex),
          ...gradebookCategoryErrors.slice(stateIndex + 1),
        ],
      });
    }
  };

  getGradingType() {
    return extractSettingAs(this.state.settings, 'gradingType', (x) =>
      _toInteger(x.value),
    );
  }

  getWeightTotal = () => {
    const { gradebookCategories } = this.state;
    let weightTotal = 0;
    gradebookCategories.forEach((category) => {
      const weight = parseInt(category.weight, 10);
      if (!isNaN(weight) && !category.__destroyed) {
        weightTotal += weight;
      }
    });
    return weightTotal;
  };

  /**
   * function that allow gets a correct index without take care the __destroyed categories
   * @param category
   * @returns {number} index to use in the category order component
   */
  getVirtualIndexFromCategories = (category) => {
    const { gradebookCategories } = this.state;
    const activeGradebookCategories = _filter(
      gradebookCategories,
      (cCategory) => !cCategory.__destroyed,
    );
    const index = _findIndex(activeGradebookCategories, category);

    return index === -1 ? activeGradebookCategories.length - 1 : index; // the current category was deleted in the front-end
  };

  updateDefaultIClickerCategory = (index, selected) => {
    this.setState((state) => ({
      gradebookCategories: state.gradebookCategories.map((category, i) => {
        if (!selected && category.is_default) {
          // If the currently selected category was deselected, revert to the default assignment category
          return {
            ...category,
            is_default_iclicker_category: true,
            __altered: true,
          };
        } else if (category.is_default_iclicker_category) {
          return {
            ...category,
            is_default_iclicker_category: false,
            __altered: true,
          };
        } else if (i === index) {
          return {
            ...category,
            is_default_iclicker_category: selected,
            __altered: true,
          };
        }
        return category;
      }),
    }));
  };

  recalculateCategoryOrder = (index) => {
    if (index === this.state.gradebookCategories.length) return; // this means the user deleted the last one category

    this.setState((state) => {
      const updatedCategoryOrders = state.gradebookCategories.map(
        (category, cIndex) => {
          if (cIndex >= index) {
            return {
              ...category,
              category_order: category.category_order - 1,
              __altered: true,
            };
          }
          return category;
        },
      );
      return {
        gradebookCategories: _sortBy(updatedCategoryOrders, 'category_order'),
      };
    });
  };

  alterCategory = (
    index,
    newSetting,
    isThisCategorySelectedForIClickerDefault,
  ) => {
    this.setState(
      (state) => ({
        gradebookCategories: state.gradebookCategories.map((category, i) => {
          if (i === index) {
            return {
              ...category,
              ...newSetting,
              __altered: true,
            };
          }
          return category;
        }),
      }),
      () => {
        if (
          _get(newSetting, '__destroyed') &&
          isThisCategorySelectedForIClickerDefault
        ) {
          this.updateDefaultIClickerCategory(index, false);
        }
        if (_get(newSetting, '__destroyed')) {
          this.recalculateCategoryOrder(index);
        }
      },
    );
  };

  addEmptyCategory = () => {
    const category = _maxBy(
      this.state.gradebookCategories,
      'category_order',
    ) || { category_order: this.state.gradebookCategories.length };
    this.setState((state) => ({
      gradebookCategories: [
        ...state.gradebookCategories,
        {
          name: '',
          drop_lowest_num_grades: 0,
          added: true,
          weight: 0,
          category_order: category.category_order + 1,
        },
      ],
    }));
  };

  alterSetting = (key, value) => {
    const { settings } = this.state;
    const settingExists = settings.find((sets) => sets.key === key);
    const newSetting = {
      key,
      value,
      __altered: true,
    };

    // If setting does not exist, append it
    if (!settingExists) {
      return this.setState((state) => ({
        settings: [...state.settings, newSetting],
      }));
    }
    // Else, update it
    return this.setState((state) => ({
      settings: state.settings.map((setting) => {
        if (setting.key === key) {
          return {
            ...setting,
            ...newSetting,
          };
        }
        return setting;
      }),
    }));
  };

  handleWeightChange = (e, input, realCategoriesStateIndex) => {
    let weight = parseInt(input, 10); // Remove leading zeros
    if (!isNaN(weight) && weight.toString().length > 3) {
      weight = weight.toString().substring(0, 3);
      weight = parseInt(weight, 10);
    }
    weight = Math.abs(weight);
    this.alterCategory(realCategoriesStateIndex, { weight });
  };

  isEmptyOrNaN(val) {
    return val === '' || isNaN(val);
  }

  isEmptyString(val) {
    const newVal = val.replace(/\s/g, '');
    return newVal === '';
  }

  async pushUpdates() {
    const {
      handleSettingsClose,
      save,
      courseId,
      isSectionManager,
      showSessionManagerOnSave,
    } = this.props;
    try {
      await save(courseId, this.state.settings, this.state.gradebookCategories);
      if (isSectionManager) {
        showSessionManagerOnSave();
      }
      return handleSettingsClose();
    } catch (err) {
      // handle error once we get client logs setup
      return handleSettingsClose();
    }
  }

  updateCategoryOrders(category, delta) {
    const { gradebookCategories } = this.state;
    const categoriesUpdated = gradebookCategories.map((cCategory) => ({
      ...cCategory,
    })); // clone items to prevent modify the react state directly
    let activeCategories = _filter(
      categoriesUpdated,
      (cCategory) => !cCategory.__destroyed,
    );
    const deletedCategories = _filter(categoriesUpdated, { __destroyed: true });
    const index = _findIndex(activeCategories, category);

    const item = activeCategories.splice(index, 1)[0];
    activeCategories.splice(index + delta, 0, item);
    activeCategories = activeCategories.map((cCategory, i) => {
      if (cCategory.category_order === i) return cCategory;

      return {
        ...cCategory,
        category_order: i,
        __altered: true,
      };
    });
    this.setState({
      gradebookCategories: activeCategories.concat(deletedCategories),
    });
  }

  renderSettingsSection() {
    const studentIdRequired = extractSettingAs(
      this.state.settings,
      'requireStudentId',
      (x) => x.value,
    );
    const isStudentIdRequiredChecked = studentIdRequired.toString() === 'true';
    const studentIdCalledVal = extractSettingAs(
      this.state.settings,
      'studentIdCalled',
    );
    const exampleIdVal = extractSettingAs(this.state.settings, 'exampleId');
    const gradesToReportVal = extractSettingAs(
      this.state.settings,
      'gradesToReport',
      (x) => _toInteger(x.value),
    );
    const roundPercentVal = extractSettingAs(
      this.state.settings,
      'roundPercentType',
      (x) => _toInteger(x.value),
    );
    const { sendSettingsEvent, courseId, isDisabled } = this.props;

    return (
      <div>
        <span className={s.title}>Grade Display</span>
        <span className={s.subTitle}>Round Percentages To:</span>
        <MLRadioButtonGroup
          className={s.gradeBookSettingsRadioGroup}
          defaultSelected={GRADEBOOK_SETTINGS.roundPercentWhole}
          selectedValue={roundPercentVal}
          onChange={(e, roundPercentType) => {
            this.alterSetting('roundPercentType', roundPercentType);
            sendSettingsEvent(changeGradeDisplay(courseId));
          }}
        >
          <MLRadioButton
            disabled={isDisabled}
            value={GRADEBOOK_SETTINGS.roundPercentWhole}
            name="roundPercentType"
            className={s.settingsRadioBtn}
            text="Whole number (100%)"
            testId="radio_button_0_decimal"
          />
          <MLRadioButton
            disabled={isDisabled}
            value={GRADEBOOK_SETTINGS.roundPercentTenths}
            name="roundPercentType"
            text="One decimal place (100.0%)"
            className={s.settingsRadioBtn}
            textClass={s.normalText}
            testId="radio_button_1_decimal"
          />
          <MLRadioButton
            disabled={isDisabled}
            value={GRADEBOOK_SETTINGS.roundPercentHundredths}
            name="roundPercentType"
            text="Two decimal places (100.00%)"
            className={s.settingsRadioBtn}
            textClass={s.normalText}
            testId="radio_button_2_decimal"
          />
        </MLRadioButtonGroup>
        <span className={s.title}>Report In-Progress Work</span>
        <MLRadioButtonGroup
          className={s.gradeBookSettingsRadioGroup}
          defaultSelected={1}
          selectedValue={gradesToReportVal}
          onChange={(e, gradesToReport) => {
            this.alterSetting('gradesToReport', gradesToReport);
            sendSettingsEvent(clickReportInProgressWork(courseId));
          }}
        >
          <MLRadioButton
            disabled={isDisabled}
            value={GRADEBOOK_SETTINGS.reportInProgressAssignments}
            name="gradebookReport"
            className={s.settingsRadioBtn}
            text="Report in-progress assignments to the gradebook prior to the due date"
            testId="in_progress_gradebook_prior_due_date"
          />
          <MLRadioButton
            disabled={isDisabled}
            value={GRADEBOOK_SETTINGS.reportOnlyDueDatePassedAssignments}
            name="gradebookReport"
            text="Report only grades for assignments after the due date has passed"
            className={s.settingsRadioBtn}
            textClass={s.normalText}
            testId="only_grades_after_due_date"
          />
        </MLRadioButtonGroup>
        <div className={s.title}>Require Student ID</div>
        <div className={`${s.normalText} ${s.studentIdWhen}`}>
          When enabled, student will be required to enter their Student ID and
          will be displayed in your gradebook.
        </div>
        <MLCheckBox
          disabled={isDisabled}
          className={`${s.checkBox} ${s.label}`}
          labelText="Require students to enter their Student ID"
          checked={isStudentIdRequiredChecked}
          testId="student-id-checkbox"
          onChange={(e, requireStudentId) => {
            this.alterSetting('requireStudentId', requireStudentId);
            sendSettingsEvent(clickRequireStudentId(courseId));
          }}
        />
        {isStudentIdRequiredChecked && (
          <div>
            <div className={s.idSection}>
              <OverLabledTextBox
                overLabelTxt="What does your institution call your student IDs?"
                placeholder="Institution ID Name"
                testId="institution-id-name"
                overLabelClassName={s.subTitle}
                disabled={!studentIdRequired || isDisabled}
                textFieldClassName={s.textOverTextBox}
                textFieldContainerClassName={s.textOverTextBoxContainer}
                value={studentIdCalledVal}
                onChange={(e, studentIdCalled) =>
                  this.alterSetting('studentIdCalled', studentIdCalled)
                }
              />
            </div>
            <div className={s.idSection}>
              <OverLabledTextBox
                overLabelTxt="Provide students an example to ensure student accuracy"
                placeholder="ID123456789"
                testId="student-id-example"
                disabled={!studentIdRequired || isDisabled}
                overLabelClassName={s.subTitle}
                textFieldClassName={s.textOverTextBox}
                textFieldContainerClassName={`${s.textOverTextBoxContainer} ${s.textOverTextBoxContainerLast}`}
                value={exampleIdVal}
                onChange={(e, exampleId) =>
                  this.alterSetting('exampleId', exampleId)
                }
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  renderCategoryListItems() {
    const { gradebookCategories } = this.state;
    const iClickerCategoryIndex =
      _findIndex(
        gradebookCategories,
        (cat) => cat.is_default_iclicker_category,
      ) || 0;
    const defaultIClickerCategoryIndex =
      iClickerCategoryIndex >= 0 ? iClickerCategoryIndex : 0;
    return _map(gradebookCategories, (gc, idx) =>
      this.renderCategoryListItem(
        gc,
        idx,
        defaultIClickerCategoryIndex,
        _filter(gradebookCategories, (category) => !category.__destroyed)
          .length,
      ),
    );
  }

  renderCategoryListItem(
    category,
    virtualStateIndex,
    defaultIClickerCategoryIndex,
    categoriesNumber,
  ) {
    const { sendSettingsEvent, courseId } = this.props;
    const gradingType = this.getGradingType();
    const { user, isSectionManager, hasInstructorLinkedCourse, isDisabled } =
      this.props;
    const isDefaultCategory = category.is_default;
    const isUngradedDefaultCategory = category.is_default_ungraded;
    const { gradebookCategoryErrors, gradebookCategories, categoryAdded } =
      this.state;
    const isThisCategorySelectedForIClickerDefault =
      defaultIClickerCategoryIndex === virtualStateIndex;
    const categoryHasError = gradebookCategoryErrors.find(
      (categoryError) => categoryError.index === virtualStateIndex,
    );
    const errorMessages = [];
    if (categoryHasError) {
      errorMessages.push(categoryHasError.errorMessage);
    }

    const dropdownLimit = 20;
    const dropLowestItems = getNCategoryAssignmentsCountDropdown(dropdownLimit);
    const defaultValue = _parseInt(
      _get(category, 'drop_lowest_num_grades', -1),
    );
    const ariaLabel = `drop_label_${virtualStateIndex}`;
    const iClickerLabel = `iclicker_label_${virtualStateIndex}`;
    const display = category.__destroyed ? 'none' : 'flex';
    const canLinkReefCourse = _get(
      user,
      'permissions.course.reef.linking.course',
      false,
    );
    const canSyncGrades = _get(
      user,
      'permissions.course.reef.gradeSync',
      false,
    );

    const { ICLICKER_INTEGRATION_DISABLED } = getConfigObject();
    const iClickerCourseLinked =
      hasInstructorLinkedCourse && canSyncGrades && canLinkReefCourse;
    const showiClicker =
      !ICLICKER_INTEGRATION_DISABLED &&
      (isSectionManager || iClickerCourseLinked);
    const activeIndexCategoryOrder =
      this.getVirtualIndexFromCategories(category);

    return (
      <li
        className={s.addCategoryLi}
        key={virtualStateIndex}
        style={{ display }}
      >
        {
          <CategoryOrder
            order={activeIndexCategoryOrder}
            disableNext={activeIndexCategoryOrder === categoriesNumber - 1}
            onUpdateCategoryOrder={(delta) =>
              this.updateCategoryOrders(category, delta)
            }
            categoryName={category.name}
            disableComponent={isDisabled}
          />
        }
        {isDefaultCategory && (
          <div className={s.defaultCategoryContainer}>
            <div className={s.subTitleDropGrades}>Category Name</div>
            <div className={s.defaultCategoryName}>Assignments</div>
            <div className={s.defaultCategoryReminder}>
              Default category, not editable
            </div>
          </div>
        )}

        {isUngradedDefaultCategory && (
          <div className={s.defaultCategoryContainer}>
            <div className={s.subTitleDropGrades}>Category Name</div>
            <div className={s.defaultCategoryName}>Ungraded</div>
            <div className={s.defaultCategoryReminder}>
              Ungraded category, not editable
            </div>
          </div>
        )}
        {!isDefaultCategory && !isUngradedDefaultCategory && (
          <OverLabledTextBox
            disabled={isDisabled}
            focus={
              categoryAdded &&
              virtualStateIndex === gradebookCategories.length - 1
            }
            overLabelContainerClassName={s.categoryTextBox}
            invalidError={categoryHasError}
            errorMessages={errorMessages}
            overLabelTxt="Category Name"
            overLabelClassName={`${s.subTitle} ${s.labelColor}`}
            textFieldClassName={s.textOverTextBox}
            textFieldContainerClassName={s.textOverTextBoxContainer}
            testId={`${category.name}-input-box`}
            value={category.name}
            onChange={(e, name) =>
              this.alterCategory(virtualStateIndex, { name })
            }
          />
        )}
        <div>
          <div
            id={ariaLabel}
            className={`${s.subTitleDropGrades} ${s.labelColor}`}
          >
            Drop Lowest Grades
          </div>
          {isDefaultCategory || isUngradedDefaultCategory ? (
            <div className={s.notAvailableText}>Not Available</div>
          ) : (
            <MLSelect
              disabled={isDisabled}
              labelledBy={ariaLabel}
              placeHolder="Select the number of lowest grades to drop"
              options={dropLowestItems}
              defaultOptionIndex={defaultValue}
              className={s.dropGrades}
              testId={`${category.name}-drop`}
              onChange={(dropLowest) =>
                this.onChangeDropLowestGrade(virtualStateIndex, dropLowest)
              }
            />
          )}
        </div>
        {gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted &&
          !isUngradedDefaultCategory && (
            <OverLabledTextBox
              disabled={isDisabled}
              overLabelContainerClassName={s.categoryTextBox}
              overLabelTxt="Weight"
              overLabelClassName={s.subTitle}
              textFieldClassName={`${s.textOverTextBox}  ${s.textOverTextBox__weight}`}
              textFieldContainerClassName={`${s.textOverTextBoxContainer} ${s.textOverTextBoxContainer__weight}`}
              /* Convert weight to a string in order to remove the leading zero. */
              value={(category.weight && category.weight.toString()) || '0'}
              onChange={(e, weight) => {
                this.handleWeightChange(e, weight, virtualStateIndex);
              }}
              type="number"
              testId={`${category.name}-weight`}
            />
          )}
        {gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted &&
          !isUngradedDefaultCategory && (
            <div className={s.weightPercent}>%</div>
          )}
        {showiClicker && !isUngradedDefaultCategory && (
          <div className={s.iclickerSyncContainer}>
            <div id={iClickerLabel} className={s.subTitleDropGrades}>
              Sync iClicker Grades to:
            </div>
            <MLCheckBox
              disabled={isDisabled}
              className={`${s.iclickerSyncCheckbox} ${s.label}`}
              checked={isThisCategorySelectedForIClickerDefault}
              onChange={(e, checked) => {
                this.updateDefaultIClickerCategory(virtualStateIndex, checked);
              }}
              testId={`${category.name}-iclicker-category`}
              labelText="Sync"
            />
          </div>
        )}
        {!isDefaultCategory && !isDisabled && !isUngradedDefaultCategory && (
          <MLButton
            className={s.trashBtn}
            ariaLabel="Remove Category"
            testId={`${category.name}-remove-category`}
            tabIndex={0}
            onClick={() => {
              this.alterCategory(
                virtualStateIndex,
                { __destroyed: true },
                isThisCategorySelectedForIClickerDefault,
              );
              sendSettingsEvent(clickRemoveCategory(courseId));
            }}
          >
            <MLIcon className={s.trashIcon} type="trash" />
          </MLButton>
        )}
      </li>
    );
  }

  renderCategoriesSection = (weightTotal) => {
    const { sendSettingsEvent, courseId, isDisabled } = this.props;
    const gradingType = this.getGradingType();

    return (
      <div className={s.section}>
        <span className={s.title}>Gradebook Categories</span>
        {this.renderGradingType()}
        <div className={s.categoryListWrapper}>
          <ol data-test-id="settings-category-list" className={s.addCategoryOl}>
            {this.renderCategoryListItems()}
          </ol>
          {gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted && (
            <div className={s.weightValidation}>
              Total:{' '}
              <b
                className={
                  weightTotal !== 100
                    ? s.weightValidationTotalError
                    : s.weightValidationTotalSuccess
                }
                data-test-id="validate-weight"
              >
                {weightTotal}%
              </b>
              <br />
              <div
                className={
                  weightTotal !== 100 ? s.weightValidationTotalError : s.hidden
                }
              >
                Weights must equal 100%
              </div>
            </div>
          )}
        </div>

        <div className={s.categoryListFooter}>
          {!isDisabled && (
            <MLButton
              btnType="secondary"
              className={s.addCatBtn}
              testId="add-category-button"
              onClick={() => {
                this.addEmptyCategory();
                sendSettingsEvent(clickAddCategory(courseId));
              }}
            >
              <MLIcon className={s.addCatIcon} type="plus" />
              <span>Add Category</span>
            </MLButton>
          )}
        </div>
      </div>
    );
  };

  renderGradingType = () => {
    const gradingType = this.getGradingType();
    const { sendSettingsEvent, courseId, isDisabled } = this.props;

    return (
      <div>
        <h2 className={s.subTitle}>Grading Type</h2>
        <MLRadioButtonGroup
          className={s.gradeBookSettingsRadioGroup}
          defaultSelected={GRADEBOOK_SETTINGS.gradingTypeStandard}
          selectedValue={gradingType}
          onChange={(e, gradesToReport) => {
            this.alterSetting('gradingType', gradesToReport);
            sendSettingsEvent(changeGradingType(courseId));
          }}
        >
          <MLRadioButton
            disabled={isDisabled}
            value={GRADEBOOK_SETTINGS.gradingTypeStandard}
            name="gradingType"
            className={`${s.settingsRadioBtn} ${s.label}`}
            text="Standard"
            testId="grading_standard"
          />
          <MLRadioButton
            disabled={isDisabled}
            value={GRADEBOOK_SETTINGS.gradingTypeWeighted}
            name="gradingType"
            text="Weighted"
            className={`${s.settingsRadioBtn} ${s.label}`}
            testId="grading_weighted"
          />
        </MLRadioButtonGroup>
        {gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted && (
          <div className={s.weightedCatsNotification}>
            <span className={s.icon}>
              <b>i</b>
            </span>
            <i className={s.note}>
              Note: Changing category weights after grades have been submitted
              could affect student scores.
            </i>
          </div>
        )}
      </div>
    );
  };

  render() {
    const {
      isOpen,
      handleSettingsClose,
      isRestrictedSection,
      handleModalExited,
      isSettingsUpdating,
      sendEvent,
      isDisabled,
      disableEnforceFocus,
    } = this.props;
    const gradingType = this.getGradingType();
    const weightTotal = this.getWeightTotal();

    return (
      <GradebookSettingsDialog
        title="Gradebook Settings"
        isOpen={isOpen}
        weightTotal={weightTotal}
        gradingType={gradingType}
        isSettingsUpdating={isSettingsUpdating}
        handleClose={handleSettingsClose}
        handleSave={this.onSave}
        handleExited={handleModalExited}
        isDisabled={isDisabled}
        disableEnforceFocus={disableEnforceFocus}
      >
        {isRestrictedSection && (
          <MessageBanner
            color="restricted"
            message="Content and settings within this course have been restricted and can only be changed from within the Section Manager."
          />
        )}
        <Card className={s.card} variant="outlined">
          {this.renderCategoriesSection(weightTotal, sendEvent)}
          {this.renderSettingsSection()}
        </Card>
      </GradebookSettingsDialog>
    );
  }
}

const mapStateToProps = (state) => ({
  rdxGradebookSettings: _get(state, 'gradebook.settings', []),
  isSettingsUpdating: _get(state, 'gradebook.isSettingsUpdating', false),
  rdxGradebookCategories: selectGradebookCategories(state),
});

const mapDispatchToProps = (dispatch) => ({
  save: (courseId, settings, categories) =>
    dispatch(
      updateGradebookSettingsAndCategories(courseId, settings, categories),
    ),
  showSessionManagerOnSave: () => {
    dispatch(
      updateAlert({
        text: 'Settings updated in restricted access sections',
        show: true,
        type: 'sync',
        dismissible: true,
        dismissTime: 8000,
      }),
    );
  },
  sendSettingsEvent: (event) => {
    dispatch(sendAnalyticsEvent(event));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GradebookSettings);
